import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { produitsService } from "@/api/services";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useI18n } from "@/i18n/I18nProvider";
import type { Product } from "@/types/domain";

export default function ProduitsPage() {
  const { t } = useI18n();

  const [produits, setProduits] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const loadProduits = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await produitsService.list();
        setProduits(data);
      } catch (err) {
        console.error("Erreur chargement produits :", err);
        setError("Impossible de charger les produits.");
      } finally {
        setLoading(false);
      }
    };

    loadProduits();
  }, []);

  if (loading) {
    return <div className="p-6">Chargement des produits...</div>;
  }

  if (error) {
    return <div className="p-6">{error}</div>;
  }

  const recherche = query.trim().toLowerCase();
  const produitsFiltres = produits.filter(
    (produit) =>
      !recherche ||
      produit.sku.toLowerCase().includes(recherche) ||
      produit.description_officielle.toLowerCase().includes(recherche)
  );

  return (
    <div className="space-y-6 p-6" data-testid="produits-page">
      <div>
        <h1 className="text-2xl font-semibold">
          {t.modules.produits.title}
        </h1>

        <p className="text-sm text-muted-foreground">
          {t.modules.produits.subtitle}
        </p>
      </div>

      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Rechercher par SKU ou description..."
        className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
      />

      {produits.length === 0 ? (
        <div className="rounded-lg border border-border p-6 text-sm text-muted-foreground">
          Aucun produit disponible.
        </div>
      ) : produitsFiltres.length === 0 ? (
        <div className="rounded-lg border border-border p-6 text-sm text-muted-foreground">
          Aucun produit ne correspond à cette recherche.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="border-b border-border">
                <th className="px-4 py-3 text-left">SKU</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-left">Famille</th>
                <th className="px-4 py-3 text-right">Stock physique</th>
                <th className="px-4 py-3 text-right">Réservé</th>
                <th className="px-4 py-3 text-right">Disponible</th>
                <th className="px-4 py-3 text-right">En commande</th>
                <th className="px-4 py-3 text-left">Emplacement</th>
                <th className="px-4 py-3 text-left">Statut</th>
              </tr>
            </thead>

            <tbody>
              {produitsFiltres.map((produit) => (
                <tr
                  key={produit.id}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-4 py-3 font-mono font-medium">
                    <Link
                      to={`/produits/${produit.id}`}
                      className="hover:underline"
                    >
                      {produit.sku}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    {produit.description_officielle}
                  </td>
                  <td className="px-4 py-3">{produit.famille || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {produit.quantite_physique}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {produit.quantite_reservee}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {produit.quantite_disponible}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {produit.quantite_en_commande}
                  </td>
                  <td className="px-4 py-3">
                    {produit.emplacement || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={produit.actif ? "Actif" : "Inactif"}
                      tone={produit.actif ? "success" : "neutral"}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
